export const PROCESS_EXTRACTION_MODE_ENV = 'PROCESS_EXTRACTION_MODE' as const;
export const KNOWLEDGE_INGESTION_ENV = 'KNOWLEDGE_INGESTION' as const;
export const BLUEPRINT_HOSTED_SYNC_ENV = 'BLUEPRINT_HOSTED_SYNC' as const;
export const FEATURE_FLAG_OVERRIDES_ENV = 'FEATURE_FLAG_OVERRIDES_JSON' as const;

export const PROCESS_EXTRACTION_MODE_VALUES = ['off', 'shadow', 'on'] as const;
export type ProcessExtractionMode = (typeof PROCESS_EXTRACTION_MODE_VALUES)[number];

export const POSTHOG_ORG_FLAG_KEYS = [
  'PROCESS_EXTRACTION_ENABLED',
  'RDM_ASSISTANT_WRITE',
  'ADDON_OVERLAY',
  'COMMERCIAL_POLICY_V3',
  // Transitional Process product-surface gate. Shared folder-data routes must
  // never branch on it; narrow Process capability flags will replace it.
  'PROCESS_REPOSITORY',
  'BLUEPRINT_ENABLED',
  'RDM_IMPORT_EXPORT',
  'COMPASS_ENABLED',
  'PARTNER_PACKS',
  'ATLAS_WORKLOAD_PLANNING',
  'ATLAS_UNIFIED_CHANGES',
  'DASHBOARD_NEW_UI',
  'PROTECTED_PROJECTION_READS',
  'PROTECTED_WRITE_GUARDS',
  'SOURCE_AUTHORITY',
  'PRIMARY_SOURCE_WRITEBACK',
] as const;

export type PostHogOrgFlagKey = (typeof POSTHOG_ORG_FLAG_KEYS)[number];

export interface ResolvedFeatureFlags {
  orgId: string;
  resolvedAt: string;
  modes: {
    PROCESS_EXTRACTION_MODE: ProcessExtractionMode;
  };
  flags: {
    PROCESS_EXTRACTION_ENABLED: boolean;
    RDM_ASSISTANT_WRITE: boolean;
    ADDON_OVERLAY: boolean;
    COMMERCIAL_POLICY_V3: boolean;
    KNOWLEDGE_INGESTION: boolean;
    PROCESS_REPOSITORY: boolean;
    BLUEPRINT_ENABLED: boolean;
    RDM_IMPORT_EXPORT: boolean;
    COMPASS_ENABLED: boolean;
    PARTNER_PACKS: boolean;
    ATLAS_WORKLOAD_PLANNING: boolean;
    /** Optional for older flag responses; absence is a dark rollout. */
    ATLAS_UNIFIED_CHANGES?: boolean;
    DASHBOARD_NEW_UI: boolean;
    PROTECTED_PROJECTION_READS: boolean;
    PROTECTED_WRITE_GUARDS: boolean;
    SOURCE_AUTHORITY: boolean;
    PRIMARY_SOURCE_WRITEBACK: boolean;
  };
}

export interface FeatureFlagEnvSource {
  PROCESS_EXTRACTION_MODE?: string;
  KNOWLEDGE_INGESTION?: string;
  BLUEPRINT_HOSTED_SYNC?: string;
  FEATURE_FLAG_OVERRIDES_JSON?: string;
  POSTHOG_PROJECT_API_KEY?: string;
  POSTHOG_HOST?: string;
  VITE_PUBLIC_POSTHOG_KEY?: string;
  VITE_PUBLIC_POSTHOG_HOST?: string;
}

export function parseProcessExtractionMode(raw: string | undefined): ProcessExtractionMode {
  const value = (raw ?? 'off').trim().toLowerCase();
  if (value === 'shadow' || value === 'on') {
    return value;
  }
  return 'off';
}

export function parseEnvBoolean(raw: string | undefined, defaultValue = false): boolean {
  if (raw === undefined || raw === '') {
    return defaultValue;
  }
  const value = raw.trim().toLowerCase();
  return value === 'on' || value === 'true' || value === '1';
}

export function canRunProcessExtraction(
  mode: ProcessExtractionMode,
  orgEnabled: boolean,
): boolean {
  if (mode !== 'off') {
    return true;
  }
  return orgEnabled;
}

export function canShowProcessExtractionUx(
  mode: ProcessExtractionMode,
  orgEnabled: boolean,
): boolean {
  if (mode === 'on') {
    return true;
  }
  return orgEnabled;
}

export function isAddOnOverlayActive(flags: ResolvedFeatureFlags): boolean {
  return flags.flags.ADDON_OVERLAY;
}

export function isCommercialPolicyV3Active(flags: ResolvedFeatureFlags): boolean {
  return flags.flags.COMMERCIAL_POLICY_V3;
}

export function resolveBlueprintEnabled(
  posthogEnabled: boolean,
  envBlueprintHostedSync?: string,
): boolean {
  if (posthogEnabled) {
    return true;
  }
  return parseEnvBoolean(envBlueprintHostedSync, false);
}

/**
 * Transitional guard for Process-specific UI and compute only.
 *
 * Never use this for Folder Sources, canonical folder-record reads/writes,
 * lifecycle, review, taxonomy, sensitivity, or permissioned projections.
 */
export function isProcessRepositoryEnabled(flags: ResolvedFeatureFlags): boolean {
  return flags.flags.PROCESS_REPOSITORY;
}

export function isBlueprintEnabled(flags: ResolvedFeatureFlags): boolean {
  return flags.flags.BLUEPRINT_ENABLED;
}

export function isRdmImportExportEnabled(flags: ResolvedFeatureFlags): boolean {
  return flags.flags.RDM_IMPORT_EXPORT;
}

export function isCompassEnabled(flags: ResolvedFeatureFlags): boolean {
  return flags.flags.COMPASS_ENABLED;
}

export function isKnowledgeIngestionEnabled(flags: ResolvedFeatureFlags): boolean {
  return flags.flags.KNOWLEDGE_INGESTION;
}

export function isAtlasWorkloadPlanningEnabled(flags: ResolvedFeatureFlags): boolean {
  return flags.flags.ATLAS_WORKLOAD_PLANNING;
}

const COMPASS_FOLDER_DOMAINS = new Set(['compass', 'grc-control', 'grc']);

export function isCompassFolderDomain(domain: string | undefined | null): boolean {
  return Boolean(domain && COMPASS_FOLDER_DOMAINS.has(domain));
}

export function shouldHideFolderDomain(
  domain: string | undefined | null,
  flags: ResolvedFeatureFlags | undefined,
): boolean {
  if (!domain) {
    return false;
  }
  const blueprintEnabled = flags ? isBlueprintEnabled(flags) : false;
  const compassEnabled = flags ? isCompassEnabled(flags) : false;
  if (domain === 'network' && !blueprintEnabled) {
    return true;
  }
  if (isCompassFolderDomain(domain) && !compassEnabled) {
    return true;
  }
  return false;
}
